import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Send, Sparkles, User, Search,
  MoreVertical, CheckCheck, Check, Clock, AlertCircle,
  Phone, Mail, Users as UsersIcon, Filter, X,
  ArrowLeft, Paperclip, Smile, Mic
} from 'lucide-react';
import { Spinner, Badge, Button } from '../../components/ui';
import { useWebSocketContext } from '../../context/WebSocketContext';
import api from '../../api/client';
import './Inbox.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function MessageStatus({ status, senderType }) {
  if (senderType === 'CUSTOMER') return null;
  if (status === 'READ')      return <CheckCheck size={12} className="status-read" />;
  if (status === 'DELIVERED') return <CheckCheck size={12} className="status-delivered" />;
  if (status === 'SENT')      return <Check size={12} className="status-sent" />;
  if (status === 'FAILED')    return <AlertCircle size={12} className="status-failed" />;
  return <Clock size={12} className="status-pending" />;
}

// ── Main Component ────────────────────────────────────────────────────────────

const Inbox = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [search, setSearch] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);

  // ── Socket moja tu ya app nzima, kutoka context ──
  const { isConnected: websocketConnected, subscribe } = useWebSocketContext();

  const messagesEndRef = useRef(null);
  const selectedIdRef = useRef(selectedId);

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get('/api/inbox/conversations');
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to load conversations', err);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedId) return;
    setLoadingMsgs(true);
    api.get(`/api/inbox/conversations/${selectedId}/messages`)
      .then(res => setMessages(res.data))
      .catch(err => console.error('Failed to load messages', err))
      .finally(() => setLoadingMsgs(false));

    api.put(`/api/inbox/conversations/${selectedId}/read`).catch(() => {});

    setConversations(prev =>
      prev.map(c => c.id === selectedId ? { ...c, unreadCount: 0 } : c)
    );
  }, [selectedId]);

  useEffect(() => {
    const unsubscribe = subscribe((data) => {
      try {
        console.log('📨 [Inbox] WebSocket message:', data.type);

        if (data.type === 'NEW_MESSAGE') {
          if (data.direction === 'INBOUND') {
            window.dispatchEvent(new CustomEvent('new-inbound-message', {
              detail: { conversationId: data.conversationId, messageId: data.messageId }
            }));
          }

          if (selectedIdRef.current === data.conversationId) {
            setMessages(prev => [...prev, {
              id: data.messageId,
              direction: data.direction,
              content: data.content,
              senderType: data.senderType,
              status: data.status,
              createdAt: data.createdAt,
            }]);
          }

          setConversations(prev => prev.map(c => {
            if (c.id !== data.conversationId) return c;
            const isOpen = selectedIdRef.current === data.conversationId;
            return {
              ...c,
              lastMessage: data.content?.substring(0, 100) || '',
              lastMessageAt: data.createdAt,
              unreadCount: (data.direction === 'INBOUND' && !isOpen)
                ? (c.unreadCount || 0) + 1
                : (c.unreadCount || 0),
            };
          }));
        }

        if (data.type === 'MODE_CHANGED') {
          setConversations(prev => prev.map(c =>
            c.id === data.conversationId ? { ...c, mode: data.mode } : c
          ));
        }

        if (data.type === 'AGENT_REQUIRED') {
          console.log('👤 Agent required for conversation:', data.conversationId);
        }
      } catch (err) {
        console.error('Error processing WebSocket message:', err);
      }
    });

    return unsubscribe;
  }, [subscribe]);

  // ── Send Reply ──────────────────────────────────────────────────────────────
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedId || sending) return;
    setSending(true);
    try {
      await api.post(`/api/inbox/conversations/${selectedId}/reply`, {
        content: replyText.trim(),
      });
      setReplyText('');
    } catch (err) {
      console.error('Failed to send reply', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  // ── Toggle AI / Human ──────────────────────────────────────────────────────
  const handleToggleMode = async (conv) => {
    const newMode = conv.mode === 'AI_HANDLING' ? 'HUMAN_HANDLING' : 'AI_HANDLING';
    try {
      await api.put(`/api/inbox/conversations/${conv.id}/mode`, { mode: newMode });
      setConversations(prev =>
        prev.map(c => c.id === conv.id ? { ...c, mode: newMode } : c)
      );
    } catch (err) {
      console.error('Failed to toggle mode', err);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const filtered = conversations.filter(c => {
    const name = c.contact?.name || c.contact?.phoneNumber || '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const selectedConv = conversations.find(c => c.id === selectedId);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="inbox-page">
      <div className="inbox-header">
        <div>
          <h1 className="inbox-title">Inbox</h1>
          <p className="inbox-subtitle">
            Customer conversations
            {websocketConnected && <span className="ws-badge online">● Live</span>}
            {!websocketConnected && <span className="ws-badge offline">● Offline</span>}
          </p>
        </div>
        <div className="inbox-actions">
          <button className="inbox-action-btn">
            <Filter size={16} />
            Filter
          </button>
        </div>
      </div>

      <div className="inbox-layout">

        {/* ── Left Panel ── */}
        <div className="conv-panel">
          <div className="conv-search">
            <Search size={15} className="conv-search-icon" />
            <input
              className="conv-search-input"
              placeholder="Search contacts..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingConvs ? (
            <div className="conv-loading"><Spinner size="sm" /></div>
          ) : filtered.length === 0 ? (
            <div className="conv-empty">
              <MessageSquare size={32} strokeWidth={1.5} />
              <p>No conversations yet</p>
              <span>Messages from your customers will appear here</span>
            </div>
          ) : (
            <div className="conv-list">
              {filtered.map(conv => (
                <div
                  key={conv.id}
                  className={`conv-item ${selectedId === conv.id ? 'active' : ''} ${conv.unreadCount > 0 ? 'unread' : ''}`}
                  onClick={() => setSelectedId(conv.id)}
                >
                  <div className="conv-avatar">
                    {getInitials(conv.contact?.name || conv.contact?.phoneNumber)}
                  </div>
                  <div className="conv-info">
                    <div className="conv-row">
                      <span className="conv-name">
                        {conv.contact?.name || conv.contact?.phoneNumber || 'Unknown'}
                      </span>
                      <span className="conv-time">{formatTime(conv.lastMessageAt)}</span>
                    </div>
                    <div className="conv-row">
                      <span className="conv-preview">
                        {conv.lastMessage || 'No messages yet'}
                      </span>
                      <div className="conv-badges">
                        {conv.mode === 'AI_HANDLING' && (
                          <span className="badge-ai"><Sparkles size={9} />AI</span>
                        )}
                        {conv.unreadCount > 0 && (
                          <span className="badge-unread">{conv.unreadCount}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right Panel ── */}
        <div className="chat-panel">
          {!selectedConv ? (
            <div className="chat-empty">
              <MessageSquare size={48} strokeWidth={1.5} className="chat-empty-icon" />
              <h3>Select a conversation</h3>
              <p>Choose a customer from the list to start chatting</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header-left">
                  <div className="chat-avatar">
                    {getInitials(selectedConv.contact?.name || selectedConv.contact?.phoneNumber)}
                  </div>
                  <div className="chat-contact-info">
                    <div className="chat-name">
                      {selectedConv.contact?.name || selectedConv.contact?.phoneNumber}
                    </div>
                    <div className="chat-phone">
                      <Phone size={12} />
                      {selectedConv.contact?.phoneNumber}
                    </div>
                  </div>
                </div>
                <div className="chat-header-right">
                  <button
                    className={`mode-toggle ${selectedConv.mode === 'AI_HANDLING' ? 'mode-ai' : 'mode-human'}`}
                    onClick={() => handleToggleMode(selectedConv)}
                    title="Toggle AI / Human mode"
                  >
                    {selectedConv.mode === 'AI_HANDLING' ? (
                      <><Sparkles size={13} /> AI</>
                    ) : (
                      <><User size={13} /> Human</>
                    )}
                  </button>
                </div>
              </div>

              {/* Message */}
              <div className="chat-messages">
                {loadingMsgs ? (
                  <div className="msgs-loading"><Spinner size="sm" /></div>
                ) : messages.length === 0 ? (
                  <div className="msgs-empty">No messages yet</div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`msg-row ${msg.direction === 'INBOUND' ? 'inbound' : 'outbound'}`}
                    >
                      <div className={`msg-bubble ${msg.senderType === 'AI' ? 'msg-ai' : ''}`}>
                        {msg.senderType === 'AI' && (
                          <div className="msg-ai-label">
                            <Sparkles size={10} /> AI Assistant
                          </div>
                        )}
                        <div className="msg-content">{msg.content}</div>
                        <div className="msg-footer">
                          <span className="msg-time">{formatTime(msg.createdAt)}</span>
                          <MessageStatus status={msg.status} senderType={msg.senderType} />
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Input */}
              <div className="chat-input-area">
                {selectedConv.mode === 'AI_HANDLING' && (
                  <div className="ai-notice">
                    <Sparkles size={13} />
                    AI is handling this conversation.
                    <button onClick={() => handleToggleMode(selectedConv)}>Take over</button>
                  </div>
                )}
                <div className="chat-input-row">
                  <textarea
                    className="chat-input"
                    placeholder="Type a message... (Enter to send)"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                  />
                  <button
                    className="send-btn"
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || sending}
                  >
                    {sending ? <Spinner size="sm" /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
};

export default Inbox;